import React, { useCallback, useMemo } from 'react'

import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalFooter from 'LuxKit/Modal/ModalFooter'

import BodyText from 'LuxKit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import PriceChangeViewV2 from 'components/Common/PriceChangeView/PriceChangeViewV2'

interface Props {
  rate: App.BedbankRate;
  newPrice?: number;
  newPropertyFees?: number;
}

function BedbankChangePriceModal(props: React.PropsWithChildren<Props>) {
  const {
    rate,
    newPrice,
    newPropertyFees,
  } = props

  const { resolve } = useModalElementContext<App.BedbankRate | undefined>()

  const onAcceptNewPrice = useCallback(() => {
    rate.totals.inclusive = newPrice ?? rate.totals.inclusive
    rate.totals.propertyFees = newPropertyFees ?? rate.totals.propertyFees

    resolve(rate)
  }, [resolve, rate, newPrice, newPropertyFees])

  const { newDisplayPrice, oldDisplayPrice, newDisplayPropertyFees, oldDisplayPropertyFees } = useMemo(() => {
    return {
      newDisplayPrice: newPrice ?? rate.totals.inclusive,
      oldDisplayPrice: rate.totals.inclusive,
      newDisplayPropertyFees: (newPropertyFees ?? rate.totals.propertyFees) ?? 0,
      oldDisplayPropertyFees: rate.totals.propertyFees ?? 0,
    }
  }, [rate, newPrice, newPropertyFees])

  return (
    <ModalBase size="S">
      <ModalHeader
        title="Price Update"
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={24}>
            <BodyText as="p" variant="large">
              The price of your package has changed due to updated availability. Please review the new price and accept it to continue.
            </BodyText>
            <PriceChangeViewV2
              newPrice={newDisplayPrice}
              oldPrice={oldDisplayPrice}
              newPropertyFees={newDisplayPropertyFees}
              oldPropertyFees={oldDisplayPropertyFees}
            />
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Accept updated price',
          onClick: onAcceptNewPrice,
        }}
        secondaryActionProps={{
          children: 'Return to offer',
        }}
      />
    </ModalBase>
  )
}

export default BedbankChangePriceModal
